import { Spin } from 'antd';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConfig, container } from 'src/inversify.config';
import { formatMessage } from 'src/core/services/http.service';
import { ListState, Query } from 'src/core/stores/data-store';
import { clone } from 'src/core/utils/object';
import { RoleAssignmentSummary, RoleAssignmentSummaryStore } from 'src/stores/role-assignments-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';

import AddUserRoleModal from './user-role-assignment-modal';
import { SearchOutlined } from '@ant-design/icons';
import { IdentityType, PermissionsConstants, ScopeType } from '../../../common/types';
import { useCurrentUserTenantPermissionsFilter } from '../../../common/permission-hooks';
import { useIdentity } from '../../../core/services/authentication';
import { LicenseApproverRoleName, LicenseManagerRoleName, StructmasterUserRoleName } from '../../../stores/roles-store';
import moment from 'moment';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { isNullOrUndefined } from 'util';
import { LicenseCreationAttributesStore } from '../../../stores/licensing-store';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat, getRoleDisplayName } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import UpdateRoleAttributesDialog from 'src/pages/role-assignments/role-attributes/update-role-attributes';
import { ValidatedSearch } from 'src/common/components/validated-search';
import { isArray } from 'lodash';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import useMediaQuery from 'src/common/hooks/use-media-query';

const permissions = [PermissionsConstants.ManageGlobalLicenses, PermissionsConstants.ManageLicenses];
const UserRolesTab: FC<{ userId: string; username: string; userOrganizations?: string[] }> = ({ userId, username, userOrganizations }) => {
  const identity = useIdentity();
  const config: AppConfiguration = useContext(AppConfig);
  const userRolesStore = container.get(RoleAssignmentSummaryStore);
  const userRolesState = userRolesStore.state;
  const [addRolesVisible, setAddRolesVisible] = useState(false);
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasGlobalLicensemanagerPermission = userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses);
  const hasLicenseManagerPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageLicenses);

  const [licenseAttrVisible, setLicenseAttrVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<RoleAssignmentSummary>();
  const [selectedRowsLength, setSelectedRowsLength] = useState<number>(0);
  const { t } = useTranslation();
  const onMobile = useMediaQuery('(max-width: 768px)');

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'Role', direction: 'Ascending', useProfile: true }],
    skip: 0,
    take: 10,
    filter: [{ and: [`(identityId eq ${userId} and Role/Name ne '${StructmasterUserRoleName}')`] }],
  } as Query);

  useEffect(() => {
    const newQuery = {
      searchQuery: '',
      orderBy: [{ field: 'Role', direction: 'Ascending', useProfile: true }],
      skip: 0,
      take: 10,
      filter: [{ and: [`(identityId eq ${userId} and Role/Name ne '${StructmasterUserRoleName}')`] }],
    } as Query;
    setQuery(newQuery);
    userRolesStore.load(newQuery);
  }, [userId, userRolesStore]);

  const onRefresh = () => {
    setSelectedAssignment(undefined);
    userRolesStore.load(query);
  };
  const expirationDateFilter = (query.filter as any)['expirationDate'];
  const hasExpirationDateFilter =
    !isNullOrUndefined(expirationDateFilter) && (!isNullOrUndefined(expirationDateFilter.ge) || !isNullOrUndefined(expirationDateFilter.le));
  const expirationDateHeader = t('ExpirationDate') + ' ' + (hasExpirationDateFilter ? '[*]' : '');
  const searchFilter = useRef<string>();

  const tableModel = {
    query,
    rowDisabled: (data) =>
      ((data.role === LicenseManagerRoleName || data.role === LicenseApproverRoleName) && !hasGlobalLicensemanagerPermission) ||
      data.role === StructmasterUserRoleName,
    columns: [
      {
        field: 'Role',
        sortable: true,
        sortField: 'Role',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={userRolesStore.baseUrl} fieldName={'Role/Name'} fieldValue={'role'} filter={`identityId eq ${userId}`} />
        ),
        fiterField: 'Role/Name',
        title: t('Role'),
        renderer: (data) => (
          <span
            className={
              ((data.role === LicenseManagerRoleName || data.role === LicenseApproverRoleName) && !hasGlobalLicensemanagerPermission) ||
              data.role === StructmasterUserRoleName
                ? 'hide-checkbox'
                : ''
            }
          >
            {getRoleDisplayName(data.role)}
          </span>
        ),
      },
      {
        field: 'ScopeTitle',
        sortable: true,
        sortField: 'ScopeTitle',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={userRolesStore.baseUrl}
            fieldName={'Scope/Title'}
            fieldValue={'scopeTitle'}
            filter={`identityId eq ${userId}`}
          />
        ),
        fiterField: 'Scope/Title',
        title: t('Scope'),
        renderer: (data) => <span>{data.scopeTitle}</span>,
      },
      {
        field: 'ScopeType',
        sortable: true,
        sortField: 'ScopeType',
        useProfile: true,
        searchable: true,
        filter: <OptionsFilter allowSearch options={Object.values(ScopeType).map((scopeType) => ({ label: t(scopeType), value: scopeType }))} />,
        fiterField: 'Scope/Type',
        title: t('ScopeType'),
        renderer: (data) => <span>{t(data.scopeType)}</span>,
      },
      {
        field: 'RoleExpirationDate',
        sortable: true,
        searchable: false,
        title: t(expirationDateHeader),
        filter: <DateRangeFilter allowClear />,
        renderer: (data) => <span>{data.expirationDate ? moment(data.expirationDate).format(getDateFormat()) : ''}</span>,
      },
      {
        field: 'UpdateRoleAttributes',
        sortable: false,
        searchable: false,
        title: '',
        renderer: (data) =>
          ((data.role === LicenseManagerRoleName || data.role === LicenseApproverRoleName) && !hasGlobalLicensemanagerPermission) ||
          (data.role === StructmasterUserRoleName && !hasLicenseManagerPermission) ? (
            ''
          ) : (
            <span
              className="clickable-span strong"
              onClick={() => {
                setSelectedAssignment(data);
                setLicenseAttrVisible(true);
              }}
            >
              {t('UpdateRoleAttributes')}
            </span>
          ),
      },
    ],
    data: userRolesState.value as ListState<RoleAssignmentSummary>,
    sortFields: [],
  } as TableModel<RoleAssignmentSummary>;

  const includeSearches = (q: Query) => {
    const nextQuery = clone<Query>(q);
    if (searchFilter.current && nextQuery.filter) {
      const scopeSearch = { and: [`contains(tolower(Scope/Title), '${searchFilter.current.toLowerCase()}')`] };
      if (isArray(nextQuery.filter)) {
        nextQuery.filter.push(scopeSearch);
      } else {
        nextQuery.filter = { ...nextQuery.filter, scopeSearch };
      }
    }
    return nextQuery;
  };
  const onSelectionChanged = (rowsKeys: string[]) => {
    setSelectedRowsLength(rowsKeys.length);
    return rowsKeys;
  };
  const onDeleteRow = async (item: RoleAssignmentSummary) => {
    let ignoreRoleForDelete = (item.role === LicenseManagerRoleName || item.role === LicenseApproverRoleName) && !hasGlobalLicensemanagerPermission;
    ignoreRoleForDelete = ignoreRoleForDelete || item.role === StructmasterUserRoleName;
    if (ignoreRoleForDelete) {
      return;
    }
    await userRolesStore.delete(item.id).then(async () => {
      if (item.role === LicenseManagerRoleName) {
        setSelectedAssignment(undefined);
        await licenseCreationAttributesStore.deleteByUserScope({
          identityId: item.identityId,
          scopeId: item.scopeId,
          identityType: IdentityType.User,
        });
      }
    });
  };
  return (
    <div className="table-small-toolbar">
      <Spin spinning={checkingPermissions || userRolesState.isBusy.get() || licenseCreationAttributesState.isBusy.get()}>
        <TableView
          selectedRowsNumber={selectedRowsLength ?? 0}
          rowKey="id"
          title={t('RolesAssigned')}
          titlePadding={'0 40px'}
          canCreateNew
          onNewItem={() => setAddRolesVisible(true)}
          canDelete={true}
          canEdit={false}
          onDeleteRow={(item: any) => onDeleteRow(item)}
          editLabel={t('EditSelected')}
          onSelectionChanged={onSelectionChanged}
          canSort
          onQueryChanged={(query: Query) => {
            const augmentedQuery = includeSearches(query);
            setQuery(query);
            userRolesStore.load(augmentedQuery);
          }}
          onRefresh={onRefresh}
          model={userRolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            userRolesState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(userRolesState.errorMessage.value) : t('UserRolesTableError'))
          }
          rightToolbar={
            <>
              <li hidden={selectedRowsLength > 0} style={{ marginRight: 5 }}>
                <ValidatedSearch
                  className="main-table-search"
                  allowClear
                  placeholder={t('SearchScope')}
                  prefix={<SearchOutlined />}
                  style={{ width: '100%', minWidth: 200, marginTop: onMobile ? 24 : undefined, marginBottom: onMobile ? 24 : undefined }}
                  onSearch={(search: string) => {
                    if (search) {
                      searchFilter.current = search;
                    } else {
                      searchFilter.current = undefined;
                    }
                    const augmentedQuery = includeSearches(query);
                    userRolesStore.load(augmentedQuery);
                  }}
                />
              </li>
            </>
          }
        />
        {addRolesVisible && (
          <AddUserRoleModal
            userId={userId}
            userOrganizations={userOrganizations}
            username={username}
            onClose={(response, addAnother) => {
              if ((response && !addAnother) || !response) {
                setAddRolesVisible(false);
              }
              onRefresh();
            }}
          />
        )}
        {licenseAttrVisible && selectedAssignment && (
          <UpdateRoleAttributesDialog
            editLicenseManagerRoleAttributes={hasGlobalLicensemanagerPermission && selectedAssignment.role === LicenseManagerRoleName}
            identityType={IdentityType.User}
            scopeId={selectedAssignment.scopeId}
            identityId={selectedAssignment.identityId}
            onClose={(newExpirationDate?: Date) => {
              if (newExpirationDate && selectedAssignment.expirationDate !== newExpirationDate) {
                userRolesStore
                  .updateExpiration(selectedAssignment.id, { scopeId: selectedAssignment.scopeId, newExpirationDate: newExpirationDate })
                  .then(() => {
                    userRolesStore.load(query);
                  });
              }
              setLicenseAttrVisible(false);
            }}
            roleExpirationDate={selectedAssignment.expirationDate}
          />
        )}
      </Spin>
    </div>
  );
};

export default UserRolesTab;
